import { ComponentProps } from 'react';
import { Button } from '@shared/components/Button';
import { Modal, useFixedWidth } from '@shared/components/Modal';
import { View } from '@shared/components/View';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { SubtableList } from './SubtableList';
import { SubtableRowMenu } from './SubtableRowMenu';
import { SubtableTable } from './SubtableTable';
import { SubtableRowMenuState } from './useSubtableRowMenu';
import styles from './SubtableTableModal.module.css';

interface Props extends Omit<
  ComponentProps<typeof SubtableTable>,
  'modal' | 'selectedRowId' | 'onRowPress'
> {
  component: API.SubtableComponent;
  workflowTemplate?: API.WorkflowTemplate;
  /** The card whose rows these are. */
  parentEntityId: string;
  /** After a row is added or saved from the modal. */
  onRowSaved: () => void;
  title: string;
  /** Hides Add row and opens rows read-only. */
  isReadOnly?: boolean;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /**
   * The modal's own row menu (separate from the field's: a menu in the covered card modal would be
   * inert). Owned by SubtableEditField so mobile's Add row can open the modal and its new-row sheet
   * together.
   */
  rowMenu: SubtableRowMenuState;
}

const SUBTABLE_FIXED_WIDTH_KEY = 'subtable-fixed-width';

/**
 * The subtable expanded into a modal over the card form. Desktop: the table in a full-page modal
 * that opens at the card modal's width (fixed or full), with its own toggle, which doesn't change
 * the card's setting. Mobile: every row in the list, in a sheet below the card's title bar.
 */
export function SubtableTableModal({
  component,
  workflowTemplate,
  parentEntityId,
  onRowSaved,
  title,
  isReadOnly = false,
  isOpen,
  onOpenChange,
  rowMenu,
  ...tableProps
}: Props) {
  const [isFixedWidth, toggleFixedWidth] = useFixedWidth(SUBTABLE_FIXED_WIDTH_KEY);
  const anchorName = getAnchorName('subtable-modal', component.id);
  const isDesktop = useIsDesktop();

  return (
    <Modal
      isOpen={isOpen}
      mode="standard"
      animate
      fullPage
      fullWidth={!isFixedWidth}
      lightDismiss
      onOpenChange={onOpenChange}
      className={styles.modal}
      style={{ anchorName }}
      testID="subtable-modal"
    >
      <Modal.TitleBar
        title={title}
        startContent={<Modal.CloseButton onClick={() => onOpenChange(false)} />}
        endContent={
          <View row centerVertically gap={0.5} paddingRight={isDesktop ? 0 : 0.5}>
            {!isReadOnly && <SubtableModalAddButton onPress={rowMenu.openNew} />}
            {isDesktop && (
              <Modal.ResizeButton isExpanded={!isFixedWidth} onClick={toggleFixedWidth} />
            )}
          </View>
        }
      />
      <div className={styles.body}>
        {isDesktop && (
          <SubtableTable
            {...tableProps}
            modal
            selectedRowId={rowMenu.selectedRowId}
            onRowPress={rowMenu.openRow}
          />
        )}
        {!isDesktop && (
          <SubtableList
            columns={tableProps.columns}
            rows={tableProps.rows}
            refs={tableProps.refs}
            hasMore={tableProps.hasMore}
            onLoadMore={tableProps.onLoadMore}
            modal
            selectedRowId={rowMenu.selectedRowId}
            onRowPress={rowMenu.openRow}
          />
        )}
      </div>
      <SubtableRowMenu
        workflowTemplate={workflowTemplate}
        parentEntityId={parentEntityId}
        isOpen={rowMenu.isOpen}
        row={rowMenu.row}
        isReadOnly={isReadOnly}
        containerAnchorName={anchorName}
        onSaved={onRowSaved}
        onClose={rowMenu.close}
      />
    </Modal>
  );
}

/** The title-bar button for opening the add-row menu. */
function SubtableModalAddButton({ onPress }: { onPress: () => void }) {
  const isDesktop = useIsDesktop();
  return (
    <Button
      variant="tertiary"
      size="medium"
      icon="plus"
      iconOnly={!isDesktop}
      label={i18n('card.blocks.subtableInput.addRow')}
      onClick={onPress}
    />
  );
}
